// src/components/stats/RegxProgressSection.js
//
// "RegX progress": headline numbers and trend charts on top of /statistics.
// Flag DEMO_STATS_ENABLED, admins only (coordinators can't read some of these
// numbers). Every number is live, and every chart names its dates.
// Math: services/demoStats.js. Reads: services/demoStatsService.js.
// Colours: theme-demo-stats.css (light and dark).

import { useEffect, useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useSiteAdmin } from "../sourcex/useSiteAdmin";
import { loadProgressNumbers } from "../../services/demoStatsService";
import {
  compareToLastYear,
  firstAndLastMonth,
  formatCount,
  monthLabel,
  monthlyPoints,
  preScreenPoints,
  reportTotalsByMonth,
  screeningYearPoints,
  toRunningTotal,
  trimLeadingZeros,
  weekLabel,
} from "../../services/demoStats";

const SERIES = "var(--rxp-series)";
const SURFACE = "var(--rxp-surface)";
const CHART_MARGIN = { top: 22, right: 40, bottom: 0, left: 0 };

const axisProps = {
  tickLine: false,
  tick: { fill: "var(--rxp-muted)", fontSize: 11 },
  stroke: "var(--rxp-grid)",
};

const ProgressTooltip = ({ active, payload, label, valueName }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rxp-tooltip">
      <div className="rxp-tooltip-label">{label}</div>
      <div className="rxp-tooltip-value">
        {formatCount(payload[0].value)} <span>{valueName}</span>
      </div>
    </div>
  );
};

/** The one number a chart labels: its last point's, above the mark. */
const endLabel = (lastIndex) => {
  const EndLabel = ({ x, y, width = 0, value, index }) =>
    index === lastIndex ? (
      <text
        x={x + width / 2}
        y={y - 10}
        textAnchor="middle"
        fill="var(--rxp-text)"
        fontSize={12}
        fontWeight={650}
      >
        {formatCount(value)}
      </text>
    ) : null;
  return EndLabel;
};

const endDot = (lastIndex) => {
  const EndDot = ({ cx, cy, index }) =>
    index === lastIndex ? (
      <circle key={index} cx={cx} cy={cy} r={4} fill={SERIES} stroke={SURFACE} strokeWidth={2} />
    ) : (
      <g key={index} />
    );
  return EndDot;
};

const TrendChart = ({ kind, title, subtitle, note, points, valueName, height = 220, wide = false }) => {
  const lastIndex = points.length - 1;
  const shared = (
    <>
      <CartesianGrid vertical={false} stroke="var(--rxp-grid)" />
      <XAxis dataKey="tick" {...axisProps} minTickGap={18} />
      <YAxis
        {...axisProps}
        axisLine={false}
        width={46}
        allowDecimals={false}
        tickFormatter={formatCount}
      />
    </>
  );
  const tooltip = (cursor) => (
    <Tooltip cursor={cursor} content={<ProgressTooltip valueName={valueName} />} />
  );
  const lineCursor = { stroke: "var(--rxp-muted)", strokeWidth: 1 };

  return (
    <figure className={`rxp-card ${wide ? "lg:col-span-2" : ""}`} data-testid={`progress-chart-${valueName}`}>
      <figcaption>
        <div className="rxp-chart-title">{title}</div>
        <div className="rxp-chart-sub">{subtitle}</div>
        {note && <div className="rxp-chart-sub">{note}</div>}
      </figcaption>
      <div className="mt-3" aria-hidden="true">
        <ResponsiveContainer width="100%" height={height}>
          {kind === "bars" ? (
            <BarChart data={points} margin={CHART_MARGIN} barCategoryGap="28%">
              {shared}
              {tooltip({ fill: "var(--rxp-wash)" })}
              <Bar
                dataKey="value"
                fill={SERIES}
                maxBarSize={24}
                radius={[4, 4, 0, 0]}
                label={endLabel(lastIndex)}
              />
            </BarChart>
          ) : kind === "area" ? (
            <AreaChart data={points} margin={CHART_MARGIN}>
              {shared}
              {tooltip(lineCursor)}
              <Area
                type="monotone"
                dataKey="value"
                stroke={SERIES}
                strokeWidth={2}
                fill={SERIES}
                fillOpacity={0.1}
                dot={endDot(lastIndex)}
                activeDot={{ r: 4, fill: SERIES, stroke: SURFACE, strokeWidth: 2 }}
                label={endLabel(lastIndex)}
              />
            </AreaChart>
          ) : (
            <LineChart data={points} margin={CHART_MARGIN}>
              {shared}
              {tooltip(lineCursor)}
              <Line
                type="monotone"
                dataKey="value"
                stroke={SERIES}
                strokeWidth={2}
                dot={endDot(lastIndex)}
                activeDot={{ r: 4, fill: SERIES, stroke: SURFACE, strokeWidth: 2 }}
                label={endLabel(lastIndex)}
              />
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>
      {/* The same numbers for screen readers, since the chart itself is a picture. */}
      <table className="sr-only">
        <thead>
          <tr>
            <th scope="col">Period</th>
            <th scope="col">{valueName}</th>
          </tr>
        </thead>
        <tbody>
          {points.map((p) => (
            <tr key={p.key}>
              <td>{p.tick}</td>
              <td>{formatCount(p.value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
};

const Tile = ({ label, value, detail }) => (
  <div className="rxp-card rxp-tile">
    <div className="rxp-tile-label">{label}</div>
    <div className="rxp-tile-value">{value}</div>
    {detail && <div className="rxp-tile-detail">{detail}</div>}
  </div>
);

const rangeText = (points, labelOf = (p) => monthLabel(p.key)) =>
  `${labelOf(points[0])} to ${labelOf(points[points.length - 1])}`;

const ProgressContent = ({ reports, now }) => {
  const [numbers, setNumbers] = useState(null);

  useEffect(() => {
    let cancelled = false;
    loadProgressNumbers(now)
      .then((result) => {
        if (!cancelled) setNumbers(result);
      })
      .catch(() => {
        if (!cancelled) setNumbers({});
      });
    return () => {
      cancelled = true;
    };
  }, [now]);

  const totals = useMemo(() => reportTotalsByMonth(reports), [reports]);

  const preScreens = preScreenPoints(totals, now);
  const screenings = screeningYearPoints(totals, now);
  const screeningYear = screenings[0]?.key.slice(0, 4);

  const heading = (
    <div>
      <h2 className="rxp-heading">RegX progress</h2>
      <p className="rxp-chart-sub">Live numbers for this site</p>
    </div>
  );

  if (!numbers) {
    return (
      <section className="rxp-section" aria-busy="true">
        {heading}
        <p className="rxp-chart-sub mt-3">Loading the latest numbers…</p>
      </section>
    );
  }

  const { patientTotal, patientsByMonth, submissionsByMonth, submissionsMoved, pft } = numbers;
  const patientsCompare = patientsByMonth && compareToLastYear(patientsByMonth, now);
  const submissionsSpan = submissionsByMonth && firstAndLastMonth(submissionsByMonth, now);
  const patientPoints = patientsByMonth ? toRunningTotal(monthlyPoints(patientsByMonth, now)) : [];
  const submissionPoints = submissionsByMonth ? monthlyPoints(submissionsByMonth, now) : [];
  const pftPoints = pft
    ? toRunningTotal(
        trimLeadingZeros(
          pft.weeks.map(({ week, value }) => ({
            key: week.toISOString(),
            tick: weekLabel(week),
            weekStart: week,
            value,
          }))
        )
      )
    : [];

  return (
    <section className="rxp-section">
      {heading}

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-2.5">
        {patientTotal && (
          <Tile label="Patients in RegX" value={formatCount(patientTotal.total)} detail="As of today" />
        )}
        {patientsCompare?.recent > 0 && (
          <Tile
            label={`Patients added, ${patientsCompare.recentRange}`}
            value={formatCount(patientsCompare.recent)}
            detail={
              patientsCompare.percent === null
                ? undefined
                : `${formatCount(patientsCompare.prior)} in ${patientsCompare.priorRange} (${
                    patientsCompare.percent >= 0 ? "+" : ""
                  }${patientsCompare.percent}%)`
            }
          />
        )}
        {submissionsSpan && (
          <Tile
            label={`Text survey submissions, ${monthLabel(submissionsSpan.lastMonth)}`}
            value={formatCount(submissionsSpan.lastValue)}
            detail={
              submissionsSpan.firstMonth === submissionsSpan.lastMonth
                ? undefined
                : `${formatCount(submissionsSpan.firstValue)} in ${monthLabel(submissionsSpan.firstMonth)}`
            }
          />
        )}
        {submissionsMoved > 0 && (
          <Tile
            label="Patients from text surveys"
            value={formatCount(submissionsMoved)}
            detail="Survey answers moved into the patient list"
          />
        )}
        {pft?.uploaded > 0 && (
          <Tile
            label="PFT reports uploaded"
            value={formatCount(pft.uploaded)}
            detail={`${formatCount(pft.read)} read, ${formatCount(pft.tests)} tests`}
          />
        )}
      </div>

      <div className="mt-2.5 grid grid-cols-1 lg:grid-cols-2 gap-2.5">
        {patientPoints.length > 0 && (
          <TrendChart
            wide
            kind="area"
            height={240}
            title="Patients added to RegX"
            subtitle={`Running total, ${rangeText(patientPoints)}`}
            note={patientTotal?.hasSheet ? "Doesn't include patients from the earlier Google Sheet" : undefined}
            points={patientPoints}
            valueName="patients"
          />
        )}
        {submissionPoints.length > 0 && (
          <TrendChart
            kind="bars"
            title="Text survey submissions"
            subtitle={`Per month, ${rangeText(submissionPoints)}`}
            points={submissionPoints}
            valueName="submissions"
          />
        )}
        {pftPoints.length > 0 && (
          <TrendChart
            kind="area"
            title="PFT reports read"
            subtitle={`Running total by week, weeks of ${rangeText(pftPoints, (p) => weekLabel(p.weekStart))}`}
            points={pftPoints}
            valueName="PFT reports"
          />
        )}
        {preScreens.some((p) => p.value > 0) && (
          <TrendChart
            kind="line"
            title="Pre-screens"
            subtitle={`Per month, ${rangeText(preScreens)}. From weekly reports`}
            points={preScreens}
            valueName="pre-screens"
          />
        )}
        {screenings.some((p) => p.value > 0) && (
          <TrendChart
            kind="area"
            title={`Screenings in ${screeningYear}`}
            subtitle={`Running total, ${rangeText(screenings)}. From weekly reports`}
            points={screenings}
            valueName="screenings"
          />
        )}
      </div>
    </section>
  );
};

const RegxProgressSection = ({ reports = [], now }) => {
  const isAdmin = useSiteAdmin();
  // One "today" for the whole visit, so the reads don't repeat on every render.
  const [today] = useState(() => now || new Date());
  if (!isAdmin) return null;
  return <ProgressContent reports={reports} now={today} />;
};

export default RegxProgressSection;
